import React from 'react';
import axios from 'axios';
import ToggleSwitch from './ToggleSwitch';
import TagInput from './TagInput';
import TrafficFilters, { buildTrafficFilters, legacyTrafficFilters } from './TrafficFilters';
import CustomFlags, { mapFlagsToForm, normalizeCustomFlags } from './CustomFlags';
import { getPlatform } from '../platforms';

export function buildInitialForm() {
  return {
    appId: '',
    appUrl: '',
    isOpen: false,
    uacApi: false,
    fbApi: false,
    asaApi: false,
    FbDrop: false,
    simulatorApi: false,
    blockOrganic: false,
    trustedCheck: false,
    bearerToken: '',
    jwToken: '',
    asaClientId: '',
    clickTokenEnabled: false,
    clickToken: '',
    uacLinkIds: [],
    uacLinkIdsText: '',
    proxyCheck: false,
    geoBlock: false,
    blockedGeos: [],
    geoUrlEnabled: false,
    geoUrl: '',
    geoUrlGeos: [],
    adjustS2S: false,
    adjustTestMode: false,
    adjustAppToken: '',
    adjustS2SToken: '',
    adspectEnabled: false,
    adspectStreamId: '',
    trafficFilters: buildTrafficFilters(),
    customFlags: [],
  };
}

export function normalizeAppFormPayload(formData, platform = 'android') {
  const base = {
    appId: formData.appId.trim(),
    appUrl: formData.appUrl.trim(),
    isOpen: !!formData.isOpen,
    uacApi: !!formData.uacApi,
    FbDrop: !!formData.FbDrop,
    simulatorApi: !!formData.simulatorApi,
    blockOrganic: !!formData.blockOrganic,
    clickTokenEnabled: !!formData.clickTokenEnabled,
    clickToken: formData.clickToken?.trim() || null,
    uacLinkIds: (formData.uacLinkIdsText || '')
      .split('\n')
      .map((v) => v.trim())
      .filter(Boolean),
    geoUrlEnabled: !!formData.geoUrlEnabled,
    geoUrl: (formData.geoUrl || '').trim(),
    geoUrlGeos: [
      ...new Set((formData.geoUrlGeos || []).map((v) => v.trim().toUpperCase()).filter(Boolean)),
    ],
    adspectEnabled: !!formData.adspectEnabled,
    adspectStreamId: (formData.adspectStreamId || '').trim(),
    customFlags: normalizeCustomFlags(formData.customFlags),
  };

  const adjust = {
    adjustS2S: !!formData.adjustS2S,
    adjustTestMode: !!formData.adjustTestMode,
    adjustAppToken: (formData.adjustAppToken || '').trim(),
    adjustS2SToken: (formData.adjustS2SToken || '').trim(),
  };

  if (platform === 'ios') {
    return {
      ...base,
      ...adjust,
      asaApi: !!formData.asaApi,
      trustedCheck: !!formData.trustedCheck,
      bearerToken: formData.bearerToken?.trim() || null,
      jwToken: formData.jwToken?.trim() || null,
      asaClientId: formData.asaClientId?.trim() || null,
    };
  }

  const trafficFilters = buildTrafficFilters(formData.trafficFilters);
  const { geo, proxyVpn } = trafficFilters.nonOrganic;

  return {
    ...base,
    ...adjust,
    fbApi: !!formData.fbApi,
    trafficFilters,
    proxyCheck: !!proxyVpn.enabled,
    geoBlock: !!geo.enabled,
    blockedGeos: [
      ...new Set((geo.blockedGeos || []).map((v) => v.trim().toUpperCase()).filter(Boolean)),
    ],
  };
}

export function mapAppToForm(app) {
  return {
    ...buildInitialForm(),
    ...app,
    bearerToken: app?.bearerToken || '',
    jwToken: app?.jwToken || '',
    asaClientId: app?.asaClientId || '',
    clickToken: app?.clickToken || '',
    uacLinkIds: Array.isArray(app?.uacLinkIds) ? app.uacLinkIds : [],
    uacLinkIdsText: Array.isArray(app?.uacLinkIds) ? app.uacLinkIds.join('\n') : '',
    blockedGeos: Array.isArray(app?.blockedGeos) ? app.blockedGeos : [],
    geoUrl: app?.geoUrl || '',
    geoUrlGeos: Array.isArray(app?.geoUrlGeos) ? app.geoUrlGeos : [],
    adjustAppToken: app?.adjustAppToken || '',
    adjustS2SToken: app?.adjustS2SToken || '',
    adspectStreamId: app?.adspectStreamId || '',
    trafficFilters: app?.trafficFilters
      ? buildTrafficFilters(app.trafficFilters)
      : legacyTrafficFilters(app),
    customFlags: mapFlagsToForm(app?.customFlags),
  };
}

function Field({ label, children }) {
  return (
    <div className="app-editor-field compact-field">
      <label className="app-editor-label compact-label">{label}</label>
      {children}
    </div>
  );
}

function SideCard({ title, children, className = '' }) {
  return (
    <div className={`app-editor-card compact-card ${className}`.trim()}>
      <div className="app-editor-card-body compact-card-body">
        <div className="app-editor-side-title compact-side-title">{title}</div>
        {children}
      </div>
    </div>
  );
}

function InlineToggle({ label, value, onChange }) {
  return (
    <label className="switch-container mb-0" style={{ width: 'auto', minWidth: 0, gap: '10px' }}>
      <span className="switch-label" style={{ marginRight: 0, flex: '0 0 auto' }}>{label}</span>
      <span className="switch">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
        <span className="slider round"></span>
      </span>
    </label>
  );
}

function clearDependents(toggles, key) {
  return toggles.reduce((acc, item) => {
    if (item.requires === key) acc[item.key] = false;
    return acc;
  }, {});
}

const GEO_URL_TOGGLE = {
  key: 'geoUrlEnabled',
  label: 'geoUrlEnabled',
  defaults: { field: 'geoUrlGeos', value: ['RU'] },
};

const ADSPECT_TOGGLE = {
  key: 'adspectEnabled',
  label: 'adspectEnabled',
};

const OPTION_TOGGLES = {
  android: [
    { key: 'isOpen', label: 'isOpen' },
    { key: 'fbApi', label: 'fbApi' },
    { key: 'uacApi', label: 'uacApi' },
    { key: 'FbDrop', label: 'FbDrop' },
    { key: 'simulatorApi', label: 'simulatorApi' },
    { key: 'blockOrganic', label: 'blockOrganic' },
    { key: 'adjustS2S', label: 'adjustS2S' },
    { key: 'adjustTestMode', label: 'adjustTestMode', requires: 'adjustS2S' },
    ADSPECT_TOGGLE,
    GEO_URL_TOGGLE,
  ],
  ios: [
    { key: 'isOpen', label: 'isOpen' },
    { key: 'asaApi', label: 'asaApi' },
    { key: 'uacApi', label: 'uacApi' },
    { key: 'FbDrop', label: 'FbDrop' },
    { key: 'simulatorApi', label: 'simulatorApi' },
    { key: 'blockOrganic', label: 'blockOrganic' },
    { key: 'trustedCheck', label: 'trustedCheck' },
    { key: 'adjustS2S', label: 'adjustS2S' },
    { key: 'adjustTestMode', label: 'adjustTestMode', requires: 'adjustS2S' },
    ADSPECT_TOGGLE,
    GEO_URL_TOGGLE,
  ],
};

export default function AppForm({ formData, setFormData, isEdit = false, section = 'general', platform = 'android' }) {
  const update = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const handleTestLink = async () => {
    try {
      const response = await axios.get('/api/test-link');
      const url = response.data?.url;

      if (!url) throw new Error('Test link is empty');

      setFormData((prev) => ({ ...prev, appUrl: url }));
    } catch (error) {
      window.alert(error.response?.data?.error || 'Не удалось получить Test link');
    }
  };

  const handleGenerateToken = async () => {
    if (!formData.appId) {
      window.alert('Сначала укажи App ID');
      return;
    }

    if (formData.clickToken?.trim()) {
      const ok = window.confirm('Для этой апки уже есть Click Token. Перегенерировать?');
      if (!ok) return;
    }

    const cfg = getPlatform(platform);
    const r = await axios.post(cfg.token(formData.appId));

    setFormData((prev) => ({ ...prev, clickToken: r.data?.clickToken || '' }));
  };

  if (section === 'facebook') {
    return (
      <div className="app-editor-card compact-card">
        <div className="app-editor-card-body compact-card-body">
          <div className="app-editor-soon">Soon</div>
        </div>
      </div>
    );
  }

  if (section === 'flags') {
    return (
      <CustomFlags
        flags={formData.customFlags || []}
        onChange={(customFlags) => setFormData((prev) => ({ ...prev, customFlags }))}
        platform={platform}
        appId={formData.appId || ''}
      />
    );
  }

  if (section === 'asa') {
    return (
      <div className="row g-3">
        <div className="col-12">
          <div className="app-editor-card compact-card">
            <div className="app-editor-card-body compact-card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <Field label="ASA Client ID">
                    <input
                      type="text"
                      value={formData.asaClientId || ''}
                      onChange={update('asaClientId')}
                      placeholder="asaClientId"
                      className="form-control app-editor-input compact-input"
                    />
                  </Field>
                </div>

                <div className="col-12">
                  <Field label="Bearer Token">
                    <textarea
                      rows={3}
                      value={formData.bearerToken || ''}
                      onChange={update('bearerToken')}
                      placeholder="bearerToken"
                      className="form-control app-editor-textarea compact-input"
                    />
                  </Field>
                </div>

                <div className="col-12">
                  <Field label="JW Token">
                    <textarea
                      rows={3}
                      value={formData.jwToken || ''}
                      onChange={update('jwToken')}
                      placeholder="jwToken"
                      className="form-control app-editor-textarea compact-input"
                    />
                  </Field>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (section === 'filters') {
    const filters = buildTrafficFilters(formData.trafficFilters);

    const setProfile = (key, profile) =>
      setFormData((prev) => ({
        ...prev,
        trafficFilters: { ...buildTrafficFilters(prev.trafficFilters), [key]: profile },
      }));

    const copyProfile = (from, to) => {
      if (!window.confirm(`Скопіювати налаштування ${from} у ${to}?`)) return;
      setProfile(to, JSON.parse(JSON.stringify(filters[from])));
    };

    return (
      <div className="row g-3 align-items-stretch">
        <div className="col-12 d-flex flex-wrap gap-2">
          <button type="button" className="fbtn" onClick={() => copyProfile('organic', 'nonOrganic')}>
            Скопіювати Organic → Non-organic
          </button>
          <button type="button" className="fbtn" onClick={() => copyProfile('nonOrganic', 'organic')}>
            Скопіювати Non-organic → Organic
          </button>
        </div>

        <div className="col-12 col-lg-6">
          <TrafficFilters
            title="Organic"
            subtitle="sub_id_1 порожній, джерело organic або невідоме"
            profile={filters.organic}
            onChange={(profile) => setProfile('organic', profile)}
          />
        </div>

        <div className="col-12 col-lg-6">
          <TrafficFilters
            title="Non-organic"
            subtitle="є sub_id_1 або джерело — рекламна мережа"
            profile={filters.nonOrganic}
            onChange={(profile) => setProfile('nonOrganic', profile)}
          />
        </div>
      </div>
    );
  }

  if (section === 'uac') {
    return (
      <div className="row g-3">
        <div className="col-12">
          <div className="app-editor-card compact-card">
            <div className="app-editor-card-body compact-card-body">
              <Field label="UAC Link IDs">
                <textarea
                  rows={12}
                  placeholder={'one id per line'}
                  value={formData.uacLinkIdsText || ''}
                  onChange={update('uacLinkIdsText')}
                  className="form-control app-editor-textarea compact-input"
                />
              </Field>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const optionToggles = OPTION_TOGGLES[platform] || OPTION_TOGGLES.android;

  return (
    <div className="row g-3 align-items-start">
      <div className="col-12 col-xl-8">
        <div className="d-flex flex-column gap-3">
          <div className="app-editor-card compact-card">
            <div className="app-editor-card-body compact-card-body">
              <div className="row g-3">
                <div className="col-12 col-md-5">
                  <Field label="App ID">
                    <input
                      type="text"
                      value={formData.appId}
                      onChange={update('appId')}
                      placeholder="com.example.app"
                      disabled={isEdit}
                      className="form-control app-editor-input compact-input"
                    />
                  </Field>
                </div>

                <div className="col-12 col-md-7">
                  <Field label="App URL">
                    <input
                      type="text"
                      value={formData.appUrl}
                      onChange={update('appUrl')}
                      placeholder="https://example.com/path"
                      className="form-control app-editor-input compact-input"
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-2">
                <button type="button" className="fbtn" onClick={handleTestLink}>
                  Test link
                </button>
              </div>
            </div>
          </div>

          {formData.adjustS2S ? (
            <div className="row g-3">
              <div className="col-12">
                <SideCard title="Adjust S2S" className="h-100">
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <Field label="App Token">
                        <input
                          type="text"
                          value={formData.adjustAppToken || ''}
                          onChange={update('adjustAppToken')}
                          placeholder="adjustToken"
                          className="form-control app-editor-input compact-input"
                        />
                      </Field>
                    </div>

                    <div className="col-12 col-md-6">
                      <Field label="S2S Token">
                        <input
                          type="text"
                          value={formData.adjustS2SToken || ''}
                          onChange={update('adjustS2SToken')}
                          placeholder="adjustTokenS2s"
                          className="form-control app-editor-input compact-input"
                        />
                      </Field>
                    </div>

                  </div>
                </SideCard>
              </div>
            </div>
          ) : null}

          {formData.adspectEnabled ? (
            <div className="row g-3">
              <div className="col-12">
                <SideCard title="Adspect" className="h-100">
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <Field label="Stream ID">
                        <input
                          type="text"
                          value={formData.adspectStreamId || ''}
                          onChange={update('adspectStreamId')}
                          placeholder="b2373d37-9a09-4e06-8860-1a06b439fbe6"
                          className="form-control app-editor-input compact-input"
                        />
                      </Field>
                    </div>
                  </div>
                </SideCard>
              </div>
            </div>
          ) : null}

          {formData.geoUrlEnabled ? (
            <div className="row g-3">
              <div className="col-12">
                <SideCard title="Geo Domain" className="h-100">
                  <div className="row g-3">
                    <div className="col-12 col-md-7">
                      <Field label="Geo URL">
                        <input
                          type="text"
                          value={formData.geoUrl || ''}
                          onChange={update('geoUrl')}
                          placeholder="https://example.com/path"
                          className="form-control app-editor-input compact-input"
                        />
                      </Field>
                    </div>

                    <div className="col-12 col-md-5">
                      <Field label="Countries">
                        <TagInput
                          value={formData.geoUrlGeos || []}
                          onChange={(v) => setFormData((prev) => ({ ...prev, geoUrlGeos: v }))}
                          placeholder="country code, Enter"
                          transform={(v) => v.toUpperCase()}
                        />
                      </Field>
                    </div>
                  </div>
                </SideCard>
              </div>
            </div>
          ) : null}

          <div className="row g-3">
            <div className="col-12">
              <SideCard title="Click Token" className="h-100">
                <input
                  type="text"
                  placeholder="Click Token"
                  value={formData.clickToken || ''}
                  readOnly
                  className="form-control app-editor-input compact-input"
                />

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                  <button type="button" className="fbtn is-primary" onClick={handleGenerateToken}>
                    Generate
                  </button>

                  <InlineToggle
                    label="clickTokenEnabled"
                    value={!!formData.clickTokenEnabled}
                    onChange={(v) => setFormData((prev) => ({ ...prev, clickTokenEnabled: v }))}
                  />
                </div>
              </SideCard>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 col-xl-4">
        <SideCard title="Options">
          <div className="ed-options">
            {optionToggles
              .filter((item) => !item.requires || formData[item.requires])
              .map((item) => (
                <ToggleSwitch
                  key={item.key}
                  className={item.requires ? 'ed-option-nested' : ''}
                  label={item.label}
                  value={!!formData[item.key]}
                  onChange={(v) =>
                    setFormData((prev) => ({
                      ...prev,
                      [item.key]: v,
                      ...(v && item.defaults && !prev[item.defaults.field]?.length
                        ? { [item.defaults.field]: item.defaults.value }
                        : null),
                      ...(v ? null : clearDependents(optionToggles, item.key)),
                    }))
                  }
                />
              ))}
          </div>
        </SideCard>
      </div>
    </div>
  );
}
